import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, Plus, Trash2, Loader2, Database, MapPin, BarChart3, Calendar, Filter, Tag, AlertTriangle } from 'lucide-react';
import { datasetApi } from '../../../api/datasetApi';
import { workspaceApi } from '../../../api/workspaceApi';
import { cn } from '../../../lib/utils';
import { getDefaultPalette } from '../../../utils/colorPalettes';

const STEPS = {
  DATASET: 0,
  SPATIAL: 1,
  TIME: 2,
  METRIC: 3,
  STYLE: 4
};

const MapLayerBuilder = ({ onSave, onPreview, onCancel, initialState = null, availableDatasets = [], workspaceId, chatId }) => {
  const [datasets, setDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [columns, setColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);

  // Form State
  const [selectedDatasetId, setSelectedDatasetId] = useState(initialState?.dataset || '');
  // Track current step for sequential reveal
  const [currentStep, setCurrentStep] = useState(initialState ? STEPS.STYLE : STEPS.DATASET);

  const [form, setForm] = useState({
    spatialColumn: initialState?.spatial?.column || '',
    valueColumn: initialState?.value?.column || '',
    aggregation: initialState?.value?.aggregation || 'sum',
    filters: initialState?.filters || [],
    palette: initialState?.config?.palette || getDefaultPalette(), // Default to viridis
    inverted: initialState?.config?.inverted || false
  });

  // Time Config State
  const [timeConfig, setTimeConfig] = useState({
    column: initialState?.time?.column || '',
    mode: initialState?.time?.start && initialState?.time?.end && initialState?.time?.start === initialState?.time?.end ? 'single' : 'range',
    start: initialState?.time?.start ? initialState.time.start.split('T')[0] : '',
    end: initialState?.time?.end ? initialState.time.end.split('T')[0] : '',
    singleDate: initialState?.time?.start ? initialState.time.start.split('T')[0] : '',
    minDate: '',
    maxDate: ''
  });


  // Autocomplete Options & Loading State
  const [filterOptions, setFilterOptions] = useState({});
  const [loadingFilters, setLoadingFilters] = useState({});

  // Time distinct values for single date mode
  const [timeDistinctValues, setTimeDistinctValues] = useState([]);
  const [loadingTimeValues, setLoadingTimeValues] = useState(false);

  // Artifact saving state
  const [savingArtefact, setSavingArtefact] = useState(false);
  const [layerName, setLayerName] = useState(initialState?.name || '');

  // Propagate default name if empty
  useEffect(() => {
    if (!layerName && selectedDatasetId && form.valueColumn) {
      const desc = `${form.aggregation.toUpperCase()} (${form.valueColumn})`;
      setLayerName(desc);
    }
  }, [selectedDatasetId, form.valueColumn, form.aggregation, layerName]);

  // Load Datasets
  useEffect(() => {
    const loadDatasets = async () => {
      // If we have availableDatasets from the workspace, use them!
      if (availableDatasets && availableDatasets.length > 0) {
        // Map available datasets to the format expected by the builder
        // The workspace sources have { id, name, type } which usually matches.
        setDatasets(availableDatasets);
        // Select first dataset by default if not editing and no dataset selected
        if (!initialState && availableDatasets.length > 0) {
          setSelectedDatasetId(prev => prev || availableDatasets[0].id);
        }
        return;
      }

      setLoadingDatasets(true);
      try {
        const data = await datasetApi.getDatasets();
        setDatasets(data);
        // Select first dataset by default if not editing and no dataset selected
        if (!initialState && data.length > 0) {
          setSelectedDatasetId(prev => prev || data[0].id);
        }
      } catch (err) {
        console.error('Failed to load datasets:', err);
      } finally {
        setLoadingDatasets(false);
      }
    };
    loadDatasets();
  }, [availableDatasets, initialState]);

  // Helper to determine input type
  const getColType = (colName, cols = columns) => {
    const col = cols.find(c => c.name === colName);
    return col ? col.type : 'STRING';
  };

  // Helper to check if column is numeric
  const isNumericColumn = (colName, cols = columns) => {
    const colType = getColType(colName, cols).toUpperCase();
    // BigQuery types
    const bigQueryTypes = ['INTEGER', 'INT64', 'FLOAT', 'FLOAT64', 'NUMERIC', 'BIGNUMERIC', 'DECIMAL'];
    // DuckDB types (used for sample datasets and uploads)
    const duckDbTypes = ['BIGINT', 'INTEGER', 'SMALLINT', 'TINYINT', 'DOUBLE', 'REAL', 'FLOAT', 'DECIMAL', 'NUMERIC'];
    // Check exact match first
    if (bigQueryTypes.includes(colType) || duckDbTypes.includes(colType)) {
      return true;
    }
    // Check if type contains numeric keywords (for variations like INT, FLOAT, etc.)
    const numericKeywords = ['INT', 'FLOAT', 'DOUBLE', 'NUMERIC', 'DECIMAL', 'REAL'];
    return numericKeywords.some(keyword => colType.includes(keyword));
  };

  const handleTimeColumnChange = React.useCallback(async (colName) => {
    setTimeConfig(prev => ({ ...prev, column: colName }));
    setTimeDistinctValues([]);
    if (!colName) {
      setCurrentStep(prev => prev < STEPS.METRIC ? STEPS.METRIC : prev);
      return;
    }
    // Advance to metric step after selecting time column
    setCurrentStep(prev => prev < STEPS.METRIC ? STEPS.METRIC : prev);

    try {
      const range = await datasetApi.getColumnRange(selectedDatasetId, colName);
      setTimeConfig(prev => ({
        ...prev,
        minDate: range.min_val,
        maxDate: range.max_val,
        // Only override values if they are empty
        start: prev.start || (range.min_val ? range.min_val.split('T')[0] : ''),
        end: prev.end || (range.max_val ? range.max_val.split('T')[0] : ''),
        singleDate: prev.singleDate || (range.min_val ? range.min_val.split('T')[0] : '')
      }));
    } catch (err) {
      console.error('Failed to load time range:', err);
    }
  }, [selectedDatasetId]);

  // Load Columns & Auto-Select
  useEffect(() => {
    if (!selectedDatasetId) {
      setColumns([]);
      return;
    }
    const loadMetadata = async () => {
      setLoadingColumns(true);
      try {
        const meta = await datasetApi.getDatasetMetadata(selectedDatasetId);
        const cols = meta.schema || [];
        setColumns(cols);

        // Auto-selection only if not editing (or if empty)
        if (!initialState) {
          // 1. Auto-detect Spatial (H3)
          const h3Col = cols.find(c => c.name.toLowerCase().includes('h3') || c.name.toLowerCase().includes('hex'));
          if (h3Col) {
            setForm(f => ({ ...f, spatialColumn: h3Col.name }));
            // Advance step if we were at dataset or spatial
            setCurrentStep(prev => prev < STEPS.TIME ? STEPS.TIME : prev);
          } else {
            // If no auto-select, at least ensure we are at spatial step
            setCurrentStep(prev => prev < STEPS.SPATIAL ? STEPS.SPATIAL : prev);
          }

          // 2. Auto-detect Time column
          const timeCol = cols.find(c =>
            c.name.toLowerCase().includes('date') ||
            c.name.toLowerCase().includes('time') ||
            c.name.toLowerCase().includes('timestamp')
          );
          if (timeCol) {
            // Auto-select time column and load its range
            handleTimeColumnChange(timeCol.name);
            // Advance to time step
            setCurrentStep(prev => prev < STEPS.TIME ? STEPS.TIME : prev);
          }
        } else {
          // Restore time bounds if editing
          if (initialState.time?.column) {
            // We need to fetch the range to populate the slider bounds
            datasetApi.getColumnRange(selectedDatasetId, initialState.time.column)
              .then(range => {
                setTimeConfig(prev => ({
                  ...prev,
                  minDate: range.min_val,
                  maxDate: range.max_val
                }));
              });
          }
        }
      } catch (err) {
        console.error('Failed to load columns:', err);
      } finally {
        setLoadingColumns(false);
      }
    };
    loadMetadata();
  }, [selectedDatasetId, initialState, handleTimeColumnChange]);


  // Load distinct date values when switching to single date mode
  const loadTimeDistinctValues = async () => {
    if (!timeConfig.column) return;

    setLoadingTimeValues(true);
    try {
      const values = await datasetApi.getDistinctValues(selectedDatasetId, timeConfig.column);
      // Sort and format dates
      const sortedDates = values
        .map(v => {
          // Handle different date formats
          const dateStr = typeof v === 'string' ? v.split('T')[0] : v;
          return dateStr;
        })
        .filter(v => v)
        .sort();

      setTimeDistinctValues(sortedDates);

      // Set first date as default if singleDate is not set
      if (sortedDates.length > 0 && !timeConfig.singleDate) {
        setTimeConfig(prev => ({ ...prev, singleDate: sortedDates[0] }));
      }
    } catch (err) {
      console.error('Failed to load distinct time values:', err);
    } finally {
      setLoadingTimeValues(false);
    }
  };

  // Load distinct values when switching to single date mode
  useEffect(() => {
    if (timeConfig.mode === 'single' && timeConfig.column && !loadingTimeValues) {
      // Only load if we don't have values yet
      if (timeDistinctValues.length === 0) {
        loadTimeDistinctValues();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeConfig.mode, timeConfig.column]);


  // --- Filter Logic ---
  const handleAddFilter = () => {
    setForm(prev => ({
      ...prev,
      filters: [...prev.filters, { column: '', op: '=', values: [''] }]
    }));
  };

  const updateFilter = (index, field, value) => {
    // Auto-load options if column changes
    if (field === 'column') {
      loadFilterOptions(index, value);
      // If switching to a STRING column, force operator to "="
      const colType = getColType(value);
      if (colType === 'STRING') {
        setForm(prev => {
          const newFilters = [...prev.filters];
          newFilters[index] = { ...newFilters[index], [field]: value, op: '=' };
          return { ...prev, filters: newFilters };
        });
        return;
      }
    }
    setForm(prev => {
      const newFilters = [...prev.filters];
      newFilters[index] = { ...newFilters[index], [field]: value };
      return { ...prev, filters: newFilters };
    });
  };

  const removeFilter = (index) => {
    setForm(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  const loadFilterOptions = async (idx, colName) => {
    const type = getColType(colName);
    if (type !== 'STRING' && type !== 'object') return;

    setLoadingFilters(prev => ({ ...prev, [idx]: true }));
    try {
      const values = await datasetApi.getDistinctValues(selectedDatasetId, colName);
      setFilterOptions(prev => ({ ...prev, [idx]: values }));
    } catch (err) {
      console.error('Failed to load filter options:', err);
    } finally {
      setLoadingFilters(prev => ({ ...prev, [idx]: false }));
    }
  };

  // --- Submission ---
  const constructPayload = () => {
    if (!selectedDatasetId || !form.spatialColumn || !form.valueColumn || !timeConfig.column) return null;

    const payload = {
      dataset: selectedDatasetId,
      spatial: { column: form.spatialColumn },
      value: {
        column: form.valueColumn,
        aggregation: form.aggregation
      },
      dimensions: [],
      filters: form.filters
        .filter(f => f.column && f.values[0])
        .map(f => ({
          column: f.column,
          op: f.op,
          values: Array.isArray(f.values) ? f.values : [f.values]
        }))
    };

    // Time is always required
    if (timeConfig.column) {
      const colType = getColType(timeConfig.column);
      const isDateType = colType === 'DATE';

      if (timeConfig.mode === 'range' && timeConfig.start && timeConfig.end) {
        payload.time = {
          column: timeConfig.column,
          start: timeConfig.start,
          end: timeConfig.end
        };
        if (!isDateType) {
          payload.time.start = `${timeConfig.start}T00:00:00`;
          payload.time.end = `${timeConfig.end}T23:59:59`;
        }

      } else if (timeConfig.mode === 'single' && timeConfig.singleDate) {
        if (isDateType) {
          payload.time = {
            column: timeConfig.column,
            start: timeConfig.singleDate,
            end: timeConfig.singleDate
          };
        } else {
          payload.time = {
            column: timeConfig.column,
            start: `${timeConfig.singleDate}T00:00:00`,
            end: `${timeConfig.singleDate}T23:59:59`
          };
        }
      }
    }

    return payload;
  };



  const handleSubmit = async () => {
    const payload = constructPayload();
    if (!payload) return;

    // Build config object with palette
    const layerConfig = {
      palette: form.palette,
      inverted: form.inverted
    };

    // Check if anything meaningful has changed before pushing a save to the gallery
    const hasChanged = !initialState ||
      layerName !== initialState.name ||
      // Compare the query spec by stripping out UI-only props from initialState
      JSON.stringify(payload) !== JSON.stringify(Object.keys(payload).reduce((acc, k) => ({ ...acc, [k]: initialState[k] }), {}));

    if (hasChanged) {
      setSavingArtefact(true);
      try {
        // Automatically save to gallery on creation/update
        await workspaceApi.saveArtefact(workspaceId, {
          title: layerName,
          type: 'map',
          spec: payload,
          config: layerConfig,
          chat_id: chatId || null
        });
      } catch (err) {
        console.error('Failed to auto-save to gallery:', err);
        alert('Note: Layer was updated on map, but failed to save to gallery: ' + (err.response?.data?.detail || err.message));
      } finally {
        setSavingArtefact(false);
      }
    }

    // Always notify parent to update local state/map, passing the stable layerId if it exists
    onSave(payload, layerName, layerConfig, initialState?.layerId);
  };


  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-20"> {/* pb-20 for fixed footer */}

      {/* 1. DATASET */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
          <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
            <Database size={12} />
          </div>
          Dataset <span className="text-red-500 font-normal normal-case">(Required)</span>
        </label>
        <div className="relative">
          <select
            value={selectedDatasetId}
            onChange={(e) => {
              setSelectedDatasetId(e.target.value);
              setTimeConfig({
                column: '',
                mode: 'range',
                start: '',
                end: '',
                singleDate: '',
                minDate: '',
                maxDate: ''
              });
              if (currentStep < STEPS.SPATIAL) setCurrentStep(STEPS.SPATIAL);
            }}
            required
            className={cn(
              "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
              !selectedDatasetId
                ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
            )}
            disabled={loadingDatasets || (initialState && true)}
          >
            <option value="">Select Dataset...</option>
            {datasets.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <Database size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* 2. SPATIAL */}
      {selectedDatasetId && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
              <MapPin size={12} />
            </div>
            Spatial Index (H3) <span className="text-red-500 font-normal normal-case">(Required)</span>
          </label>
          {currentStep >= STEPS.SPATIAL && (() => {
            // Filter to only spatial columns
            const spatialColumns = columns.filter(c =>
              c.name.toLowerCase().includes('h3') || c.name.toLowerCase().includes('hex')
            );
            const hasSpatialColumns = spatialColumns.length > 0;

            return (
              <div className="space-y-2">
                {!hasSpatialColumns && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-bold text-amber-900 dark:text-amber-200 uppercase tracking-tight">
                        No Spatial Columns Found
                      </p>
                      <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
                        This dataset doesn't contain any spatial columns (H3, geo, spatial, location, or geometry). A spatial column is required to create a map layer.
                      </p>
                    </div>
                  </div>
                )}
                <div className="relative">
                  <select
                    value={form.spatialColumn}
                    onChange={(e) => {
                      setForm(f => ({ ...f, spatialColumn: e.target.value }));
                      setCurrentStep(prev => prev < STEPS.TIME ? STEPS.TIME : prev);
                    }}
                    required
                    className={cn(
                      "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
                      !form.spatialColumn
                        ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                        : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                    )}
                    disabled={loadingColumns || !hasSpatialColumns}
                  >
                    <option value="">{hasSpatialColumns ? "Select Column..." : "No Spatial Columns Available"}</option>
                    {spatialColumns.map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* 3. TIME */}
      {form.spatialColumn && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
              <Calendar size={12} />
            </div>
            Time <span className="text-red-500 font-normal normal-case">(Required)</span>
          </label>
          {currentStep >= STEPS.TIME && (
            <div className="space-y-3">
              <div className="relative">
                <select
                  value={timeConfig.column}
                  onChange={(e) => handleTimeColumnChange(e.target.value)}
                  required
                  className={cn(
                    "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
                    !timeConfig.column
                      ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                      : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  )}
                >
                  <option value="">Select Time Column...</option>
                  {columns.filter(c =>
                    c.name.toLowerCase().includes('date') ||
                    c.name.toLowerCase().includes('time') ||
                    c.name.toLowerCase().includes('timestamp')
                  ).map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              {timeConfig.column && (
                <div className="p-4 bg-gradient-to-br from-gray-50/50 to-white dark:from-gray-900/30 dark:to-gray-900/50 rounded-2xl border-2 border-gray-100 dark:border-gray-800 space-y-4 shadow-sm">
                  <div className="flex bg-white dark:bg-gray-900 rounded-xl border-2 border-gray-100 dark:border-gray-800 p-1 shadow-sm">
                    <button
                      onClick={() => setTimeConfig(t => ({ ...t, mode: 'range' }))}
                      className={cn(
                        "flex-1 text-xs font-bold py-2.5 rounded-lg transition-all uppercase tracking-wider",
                        timeConfig.mode === 'range'
                          ? "bg-emerald-500 text-white shadow-md"
                          : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      Date Range
                    </button>
                    <button
                      onClick={() => {
                        setTimeConfig(t => ({ ...t, mode: 'single' }));
                        // Load distinct values when switching to single date mode
                        if (timeConfig.column && timeDistinctValues.length === 0) {
                          loadTimeDistinctValues();
                        }
                      }}
                      className={cn(
                        "flex-1 text-xs font-bold py-2.5 rounded-lg transition-all uppercase tracking-wider",
                        timeConfig.mode === 'single'
                          ? "bg-emerald-500 text-white shadow-md"
                          : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      Single Date
                    </button>
                  </div>

                  {timeConfig.mode === 'range' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={timeConfig.start}
                        onChange={(e) => setTimeConfig(t => ({ ...t, start: e.target.value }))}
                        className="flex-1 text-sm px-4 py-2.5 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all font-medium text-gray-900 dark:text-white shadow-sm"
                      />
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
                        <ChevronRight size={14} />
                      </div>
                      <input
                        type="date"
                        value={timeConfig.end}
                        onChange={(e) => setTimeConfig(t => ({ ...t, end: e.target.value }))}
                        className="flex-1 text-sm px-4 py-2.5 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all font-medium text-gray-900 dark:text-white shadow-sm"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {loadingTimeValues ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 size={20} className="animate-spin text-emerald-500" />
                          <span className="ml-2 text-xs text-gray-500 font-medium">Loading available dates...</span>
                        </div>
                      ) : timeDistinctValues.length > 0 ? (
                        <>
                          <div className="flex items-center justify-between text-xs font-bold text-gray-600 dark:text-gray-400 px-1">
                            <span>{timeDistinctValues[0]}</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-black text-sm">
                              {timeConfig.singleDate || timeDistinctValues[0]}
                            </span>
                            <span>{timeDistinctValues[timeDistinctValues.length - 1]}</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={timeDistinctValues.length - 1}
                            step={1}
                            value={timeConfig.singleDate
                              ? timeDistinctValues.indexOf(timeConfig.singleDate)
                              : 0}
                            onChange={(e) => {
                              const index = parseInt(e.target.value);
                              const selectedDate = timeDistinctValues[index];
                              if (selectedDate) {
                                setTimeConfig(t => ({ ...t, singleDate: selectedDate }));
                              }
                            }}
                            className="w-full h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 accent-emerald-500 cursor-pointer shadow-inner"
                          />
                          <div className="text-center">
                            <span className="text-[10px] text-gray-500 font-medium">
                              {timeDistinctValues.length} available date{timeDistinctValues.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-xs text-gray-400">No dates available</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 4. METRIC */}
      {(form.spatialColumn && timeConfig.column) && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
              <BarChart3 size={12} />
            </div>
            Metric <span className="text-red-500 font-normal normal-case">(Required)</span>
          </label>
          {currentStep >= STEPS.METRIC && (
            <div className="flex gap-3">
              <div className="relative flex-1">
                <select
                  value={form.valueColumn}
                  onChange={(e) => {
                    setForm(f => ({ ...f, valueColumn: e.target.value }));
                  }}
                  required
                  className={cn(
                    "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
                    !form.valueColumn
                      ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                      : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  )}
                >
                  <option value="">Select Column...</option>
                  {columns.filter(c => isNumericColumn(c.name)).map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <BarChart3 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              <select
                value={form.aggregation}
                onChange={(e) => setForm(f => ({ ...f, aggregation: e.target.value }))}
                className="px-4 py-3 rounded-2xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all cursor-pointer font-bold text-xs text-gray-900 dark:text-white uppercase tracking-wider shadow-sm min-w-[100px]"
              >
                <option value="sum">SUM</option>
                <option value="avg">AVG</option>
                <option value="min">MIN</option>
                <option value="max">MAX</option>
                <option value="count">COUNT</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* 5. STYLE, FILTERS & NAMING (Visible after Value selected) */}
      {form.valueColumn && (
        <div className="space-y-6 animate-in fade-in slide-in-from-top-2 pt-6 border-t-2 border-gray-100 dark:border-gray-800">
          {/* Layer Name */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
              <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
                <Tag size={12} />
              </div>
              Layer Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={layerName}
                onChange={e => setLayerName(e.target.value)}
                placeholder="Give this layer a name..."
                className="w-full text-sm font-bold uppercase tracking-tight py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all placeholder:text-gray-400 text-gray-900 dark:text-white shadow-sm"
              />
              <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Filters */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
                <div className="p-1.5 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg">
                  <Filter size={12} />
                </div>
                Data Filters
              </label>
              <button
                onClick={handleAddFilter}
                className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 px-3 py-1.5 rounded-xl transition-all"
              >
                <Plus size={14} /> Add Filter
              </button>
            </div>

            <div className="space-y-2.5">
              {form.filters.map((filter, idx) => (
                <div key={idx} className="flex gap-2 items-center p-3 bg-gradient-to-br from-gray-50/50 to-white dark:from-gray-900/30 dark:to-gray-900/50 rounded-xl border-2 border-gray-100 dark:border-gray-800 shadow-sm">
                  <select
                    value={filter.column}
                    onChange={e => updateFilter(idx, 'column', e.target.value)}
                    className="flex-1 text-sm py-2 px-3 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm"
                  >
                    <option value="">Column</option>
                    {columns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <select
                    value={filter.op}
                    onChange={e => updateFilter(idx, 'op', e.target.value)}
                    disabled={filter.column && getColType(filter.column) === 'STRING' && filter.op !== '='}
                    className={cn(
                      "w-16 text-xs font-bold py-2 px-2 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all cursor-pointer text-gray-900 dark:text-white uppercase tracking-wider shadow-sm",
                      filter.column && getColType(filter.column) === 'STRING' && filter.op !== '=' && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <option value="=">=</option>
                    {filter.column && getColType(filter.column) !== 'STRING' && (
                      <>
                        <option value="!=">!=</option>
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                      </>
                    )}
                  </select>
                  <div className="flex-1 relative">
                    <input
                      list={`suggest - ${idx} `}
                      value={filter.values[0]}
                      onChange={e => updateFilter(idx, 'values', [e.target.value])}
                      className="w-full text-sm py-2 px-3 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-medium text-gray-900 dark:text-white shadow-sm"
                      placeholder="Value..."
                    />
                    <datalist id={`suggest - ${idx} `}>
                      {(filterOptions[idx] || []).map((v, i) => <option key={i} value={v} />)}
                    </datalist>
                    {loadingFilters[idx] && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 size={14} className="animate-spin text-emerald-500" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeFilter(idx)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {form.filters.length === 0 && (
                <div className="p-6 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-800">
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">No filters added. Click "Add Filter" to get started.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Fixed Footer Buttons */}
      <div className="sticky bottom-0 left-0 right-0 pt-6 pb-0 bg-gradient-to-t from-white via-white to-transparent dark:from-gray-950 dark:via-gray-950 border-t-2 border-gray-100 dark:border-gray-800 flex gap-3 mt-8 z-10">
        <button
          onClick={handleSubmit}
          disabled={!selectedDatasetId || !form.spatialColumn || !form.valueColumn || !timeConfig.column || savingArtefact}
          className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-wider rounded-xl shadow-lg hover:shadow-xl transition-all transform active:scale-[0.98] flex items-center justify-center gap-2"
        >
          {savingArtefact ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check size={18} />
              {initialState ? 'Update Layer' : 'Create Layer'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MapLayerBuilder;
